import {
  ApplicationRef,
  ComponentRef,
  Injectable,
  inject,
  signal,
} from '@angular/core';
import { VirtualTourComponent } from '../components/virtual-tour/virtual-tour.component';
import { TourStep } from '../models/tour-step.model';
import { createComponent } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class VirtualTourService {
  /* ---------- DI + state ---------------------------------------- */
  #appRef = inject(ApplicationRef);
  #hostRef?: ComponentRef<VirtualTourComponent>;

  currentIndex = signal(0);
  steps        = signal<TourStep[]>([]);
  isActive     = signal(false);

  /* ---------- scroll lock helpers ------------------------------- */
  private readonly scrollLockCls = 'tour-lock';
  // This method is used to lock scroll
  private lockScroll()   { document.body.classList.add(this.scrollLockCls); }
  // This method is used to unlock scroll
  private unlockScroll() { document.body.classList.remove(this.scrollLockCls); }

  /* ---------- PUBLIC API ---------------------------------------- */
  // This method is used to start using steps
  start(steps: TourStep[]): void {
    if (!steps.length) return;

    /* 1. unlock → scrollIntoView can move the page */
    this.unlockScroll();

    /* 2. shove data into signals */
    this.steps.set(steps);
    this.currentIndex.set(0);
    this.isActive.set(true);

    /* 3. lazy-create overlay (createComponent pattern ⬇︎) */
    if (!this.#hostRef) {
      queueMicrotask(() => {
        this.#hostRef = createComponent(VirtualTourComponent, {
          environmentInjector: this.#appRef.injector,   // inherits root
        });
        document.body.appendChild(this.#hostRef.location.nativeElement);
        this.#appRef.attachView(this.#hostRef.hostView);
      });
    }

    /* 4. centre first target → relock after scroll */
    // this.#scrollToStep(0);
  }

  // This method is used to handle the next callback
  next(): void {
    const idx = this.currentIndex() + 1;
    if (idx < this.steps().length) {
        this.currentIndex.set(idx);
      this.#scrollToStep(idx);
      
    } else {
      this.finish();
    }
  }

  // This method is used to finish
  finish(): void {
    this.unlockScroll();
    this.isActive.set(false);

    if (this.#hostRef) {
      this.#appRef.detachView(this.#hostRef.hostView);
      this.#hostRef.destroy();
      this.#hostRef = undefined;
    }
  }

  /* ---------- PRIVATE ------------------------------------------- */
  // This method is used to scroll to step using index
  #scrollToStep(index: number) {
    const el = document.querySelector(
      this.steps()[index].target
    ) as HTMLElement | null;

    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      /* give the browser ~400 ms, then lock again */
      setTimeout(() => this.lockScroll(), 400);
    }
  }
}
